import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { AnimatePresence, motion } from 'motion/react';
import { Trans, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text, useDebouncedValue } from '@frontend/design-system';
import { useTypingIndicatorStore } from '../stores/typing-indicator-store';

type SMSTypingIndicatorProps = { threadId: string; groupId: string };

export const SMSTypingIndicator = ({ threadId, groupId }: SMSTypingIndicatorProps) => {
  const { t } = useTranslation('thread-sending-area');
  const { getThreadTypingUsers } = useTypingIndicatorStore();
  const typingUsers = getThreadTypingUsers({ locationId: groupId, threadId });
  const show = typingUsers.length > 0;
  const [firstName, setFirstName] = useState(typingUsers.at(0)?.firstName ?? '');
  const debouncedShow = useDebouncedValue(show, 500);

  useEffect(() => {
    if (typingUsers.length > 0) {
      setFirstName(typingUsers.at(0)?.firstName ?? '');
    }
  }, [JSON.stringify(typingUsers)]);

  useEffect(() => {
    if (!debouncedShow) {
      setFirstName('');
    }
  }, [debouncedShow]);
  const remainingCount = Math.max(typingUsers.length - 1, 0);

  const backgroundColor = `${theme.colors.white}BF`;
  const spanStyles = css({
    height: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    zIndex: theme.zIndex.highest,
  });
  const textStyles = css({
    padding: theme.spacing(1, 2),
    borderRadius: theme.borderRadius.medium,
    backgroundColor,
    position: 'absolute',
    bottom: 0,
  });

  return (
    <AnimatePresence>
      {show && !!threadId && (
        <motion.span
          css={spanStyles}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {!!remainingCount ? (
            <Trans t={t} firstName={firstName} count={remainingCount}>
              <Text css={textStyles}>
                {{ firstName }} and {{ remainingCount }} others are typing...
              </Text>
            </Trans>
          ) : (
            <Trans t={t} firstName={firstName}>
              <Text css={textStyles}>{{ firstName }} is typing...</Text>
            </Trans>
          )}
        </motion.span>
      )}
    </AnimatePresence>
  );
};
